import { useRef, useState } from "react";
import { syncApiUrl } from "@cliqsuite/db";
import { useAuth } from "../../lib/auth";

/**
 * Soft-gate reminder — email verification never blocks login (see
 * services/sync-api's owner-signup/owner-login docs), this just nags until
 * it happens. Persistent (no dismiss button) since re-showing every session
 * is the point; only ever renders for the currently signed-in user whose
 * own email_verified_at is still null.
 */
export function VerificationBanner() {
  const { user } = useAuth();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  if (!user || user.email_verified_at) return null;

  const resend = async () => {
    setSending(true);
    try {
      await fetch(syncApiUrl("/auth/resend-verification"), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: user.email }),
      });
    } catch {
      // Best-effort — the endpoint itself always reports success regardless
      // (no-enumeration contract), so there's nothing more specific to show
      // even on a network error here.
    } finally {
      setSending(false);
      setSent(true);
      clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(() => setSent(false), 5000);
    }
  };

  return (
    <div className="o-banner">
      <span className="ms" aria-hidden="true">mail</span>
      <span style={{ flex: 1 }}>Verify {user.email} to keep your account secure.</span>
      <button type="button" className="o-banner__btn" onClick={resend} disabled={sending || sent}>
        {sent ? "Sent, check your inbox" : sending ? "Sending…" : "Resend email"}
      </button>
    </div>
  );
}
